import api from './api'
import { API_ENDPOINTS } from '../config/api'

// Buffer Time + Bet Limits — owner-only. Every endpoint here is refused for
// staff sessions by requireOwnerOr(key) on the server (an owner executive passes
// only with that key); the sidebar and
// OWNER_PERMISSIONS hide the screens on the client.
const bettingConfigService = {
  // ── Buffer Time ──
  getSportsBuffers: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.BUFFER_SPORTS)
    return response.data
  },
  // {
  //   markets: [{ key, label, group, hint }], default_league_key, max_seconds,
  //   sports: [{ sport_id, name, enabled, leagues: [
  //     { key, label, is_default, configured_count,
  //       cells: { matchodds: { state: 'set'|'inherit', seconds, inherited } } }
  //   ]}]
  // }
  // { sport_id, league_key, market_key, buffer_seconds } — 0 = the site default.
  saveSportsBuffer: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.BUFFER_SPORTS, payload)
    return response.data
  },
  // { items: [{ sport_id, league_key, market_key, buffer_seconds }, …] } — every
  // dirty cell in one call.
  saveSportsBufferBulk: async (items) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.BUFFER_SPORTS_BULK, { items })
    return response.data
  },
  // { sport_id, league_key, market_key } — drops a league override so the cell
  // inherits the sport's main tab again. Never valid on the default league.
  resetSportsBuffer: async (payload) => {
    const response = await api.delete(API_ENDPOINTS.BETTING_CONFIG.BUFFER_SPORTS, { data: payload })
    return response.data
  },
  getCasinoBuffers: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.BUFFER_CASINO)
    return response.data
  },
  // { game_name, buffer_seconds }
  saveCasinoBuffer: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.BUFFER_CASINO, payload)
    return response.data
  },

  // ── Bet Limits ──
  // Live + upcoming events from the feed; optional sport_id narrows it.
  getInplayEvents: async (sportId) => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.LIMITS_INPLAY, {
      params: sportId ? { sport_id: sportId } : {},
    })
    return response.data
  },
  // Every market of an event with the feed's own min/max alongside the override.
  getEventMarkets: async (eventId, sportId) => {
    const response = await api.get(`${API_ENDPOINTS.BETTING_CONFIG.LIMITS_EVENT}/${eventId}`, {
      params: sportId ? { sport_id: sportId } : {},
    })
    return response.data
  },
  // Send '' on a side to clear it and fall back to the feed value.
  saveEventLimit: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.LIMITS_EVENT, payload)
    return response.data
  },
  deleteEventLimit: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.BETTING_CONFIG.LIMITS_EVENT}/${id}`)
    return response.data
  },
  getCasinoLimits: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.LIMITS_CASINO)
    return response.data
  },
  // { game_name, min_stake, max_stake }
  saveCasinoLimit: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.LIMITS_CASINO, payload)
    return response.data
  },
  deleteCasinoLimit: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.BETTING_CONFIG.LIMITS_CASINO}/${id}`)
    return response.data
  },

  // ── Max Odds ──
  // { categories, max_value, limited_overs_sport_id,
  //   sports: [{ sport_id, name, enabled, rules: { match_odds: { max_odds, limited_overs_only }, bookmaker } }] }
  getSportsMaxOdds: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_SPORTS)
    return response.data
  },
  // Each sport also carries `leagues: [{ key, label, is_default, rules: {
  //   match_odds: { state: 'set'|'inherit', max_odds, limited_overs_only, inherited } } }]`.
  // { sport_id, league_key, category: 'match_odds'|'bookmaker', max_odds, limited_overs_only }
  // max_odds 0 = no cap (on a league tab: no cap for that league);
  // limited_overs_only is honoured for cricket only.
  saveSportsMaxOdds: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_SPORTS, payload)
    return response.data
  },
  // { sport_id, league_key, category } — drops a league override so the cell
  // follows the sport's cap again. Never valid on the default league.
  resetSportsMaxOdds: async (payload) => {
    const response = await api.delete(API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_SPORTS, { data: payload })
    return response.data
  },
  // Per-market caps — { sport_id, market_name, max_odds, test_max_odds } (upsert by sport + name)
  saveMaxOddsMarketCap: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_MARKETS, payload)
    return response.data
  },
  deleteMaxOddsMarketCap: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_MARKETS}/${id}`)
    return response.data
  },
  // { names: [...] } — odds markets bet on in this sport in the last 90 days, most used first
  getMaxOddsMarketNames: async (sportId) => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.MAX_ODDS_MARKET_NAMES, { params: { sport_id: sportId } })
    return response.data
  },

  // Auto Void — { enabled, delay_seconds, on_match_odds, on_bookmaker,
  // min_delay_seconds, max_delay_seconds }. PUT accepts any subset.
  getAutoVoid: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.AUTO_VOID)
    return response.data
  },
  saveAutoVoid: async (payload) => {
    const response = await api.put(API_ENDPOINTS.BETTING_CONFIG.AUTO_VOID, payload)
    return response.data
  },
  // Pending Result Tracker — events whose MO/BM is declared but still hold
  // unsettled bets, with void_at / seconds_left per event.
  getAutoVoidTracker: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.AUTO_VOID_TRACKER)
    return response.data
  },
  // Result Source Links — look-up sites shown in the tracker. url may use
  // {match} / {market}; sport_id null = all sports.
  getResultLinks: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.RESULT_LINKS)
    return response.data
  },
  createResultLink: async (payload) => {
    const response = await api.post(API_ENDPOINTS.BETTING_CONFIG.RESULT_LINKS, payload)
    return response.data
  },
  updateResultLink: async (id, payload) => {
    const response = await api.put(`${API_ENDPOINTS.BETTING_CONFIG.RESULT_LINKS}/${id}`, payload)
    return response.data
  },
  deleteResultLink: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.BETTING_CONFIG.RESULT_LINKS}/${id}`)
    return response.data
  },

  // Virtual Bet Limits — [{ sport_id, sport_name, league_key, label,
  // min_stake, max_stake }]. PUT { rows: [...] }; 0 = not set.
  getVirtualLimits: async () => {
    const response = await api.get(API_ENDPOINTS.BETTING_CONFIG.VIRTUAL_LIMITS)
    return response.data
  },
  saveVirtualLimits: async (payload) => {
    const response = await api.put(API_ENDPOINTS.BETTING_CONFIG.VIRTUAL_LIMITS, payload)
    return response.data
  },
}

export default bettingConfigService
